import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, filter } from 'rxjs/operators';
import { UserService } from '../../../services/user.service';
import { onUserColourCreated } from '../../../services/annotation.service';
import { EmitterService } from '../../../services/emitter.service';
import { GeneralUtilsService } from '../../../services/general-utils.service';
import { SimpleUser, User } from '../../../interfaces/user.interface';
import { onLoadProject } from '../../../services/project.service';

/** delay before reloading avatar after change event */
const AVATAR_RELOAD_DELAY_MS: number = 3500;

@Component({
  selector: 'mtm-user-profile-picture',
  templateUrl: './mtm-user-profile-picture.component.html',
  styleUrl: './mtm-user-profile-picture.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UserProfilePictureComponent implements OnInit, OnChanges {
  private readonly userService: UserService = inject(UserService);

  private readonly generalUtilsService: GeneralUtilsService = inject(GeneralUtilsService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  @Input() username: string = '';
  @Input() user: SimpleUser | User | null = null;
  @Input() avatarUrl: string = '';
  @Input() height: number = 32;
  @Input() width: number = 32;
  @Input() squareIcon: boolean = false;
  @Input() fontSize: string = '12px';
  @Input() disabled: boolean = false;
  @Input() showBorder: boolean = false;

  resolvedAvatarUrl: string = '';
  initialName: string = '';
  nameColor: string = '';
  avatarLoaded: boolean = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['username'] || changes['user'] || changes['avatarUrl']) {
      this.loadUserProfile();
    }
  }

  ngOnInit(): void {
    this.subscribeToAvatarChanges();
    this.loadUserProfile();
  }

  get alt(): string {
    return this.user?.fullName || this.username || '';
  }

  get hasProfilePicture(): boolean {
    return !!this.resolvedAvatarUrl;
  }

  private loadUserProfile(): void {
    // priority 1: direct avatarUrl input (skip placeholder images)
    if (this.avatarUrl && !this.avatarUrl.includes('avatar-no-image.png')) {
      this.resolvedAvatarUrl = this.avatarUrl;
      this.setInitialsFromUser(this.user);
      this.emitUserColour();
      this.cdr.markForCheck();
      return;
    }

    // priority 2: use provided user object
    if (this.user?.avatarUrl) {
      this.resolvedAvatarUrl = this.user.avatarUrl;
      this.setInitialsFromUser(this.user);
      this.emitUserColour();
      this.cdr.markForCheck();
      return;
    }

    // priority 3: Find user in service cache
    if (this.username) {
      const cachedUser: SimpleUser | undefined = this.userService.getCachedUserByUsername(this.username);
      if (cachedUser) {
        this.resolvedAvatarUrl = this.userService.getAvatarUrl(this.username);
        this.setInitialsFromUser(cachedUser);
        this.emitUserColour();
        this.cdr.markForCheck();
        return;
      }
    }

    // priority 4: Fallback to initials from username
    this.resolvedAvatarUrl = this.username ? this.userService.getAvatarUrl(this.username) : '';
    this.setInitialsFromUsername();
    this.emitUserColour();
    this.cdr.markForCheck();
  }

  private setInitialsFromUser(user: SimpleUser | User | null): void {
    if (!user) {
      this.setInitialsFromUsername();
      return;
    }
    const fullName: string = user.fullName || ((user as User).firstName || '') + ' ' + ((user as User).lastName || '');
    this.initialName = this.getInitials(fullName) || this.username?.substring(0, 2).toLowerCase() || '';
    this.nameColor = this.stringToHslColor(this.initialName);
  }

  private setInitialsFromUsername(): void {
    this.initialName = this.username?.substring(0, 2).toLowerCase() || '';
    this.nameColor = this.stringToHslColor(this.initialName);
  }

  private emitUserColour(): void {
    if (this.username) {
      onUserColourCreated.emit({
        act: 'onUserColourCreated',
        username: this.username,
        nameColor: this.nameColor || 'rgb(169, 169, 169)',
      });
    }
  }

  private subscribeToAvatarChanges(): void {
    EmitterService.get('authUser.changeAvatar')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((data: { externalUser?: boolean; email?: string; user?: { email: string } }) => {
          return data?.externalUser || data?.email === this.username;
        }),
        debounceTime(AVATAR_RELOAD_DELAY_MS)
      )
      .subscribe((data: { externalUser?: boolean; user?: { email: string } }) => {
        const avatarUrl: string =
          data?.externalUser && data.user ? this.userService.getAvatarUrl(data.user.email) : this.resolvedAvatarUrl;
        this.reloadProfilePicture(avatarUrl);
      });

    onLoadProject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.avatarLoaded) {
        return;
      }
      this.loadUserProfile();
    });
  }

  private reloadProfilePicture(avatarUrl: string): void {
    if (!avatarUrl) {
      return;
    }
    this.resolvedAvatarUrl = this.generalUtilsService.getCacheBusterUrl(avatarUrl);
    this.avatarLoaded = false;
    this.cdr.markForCheck();
  }

  private stringToHslColor(initial: string = ''): string {
    let hash: number = 0;
    if (!initial) {
      return '';
    }
    // Use for...of to properly iterate over Unicode code points
    for (const char of initial) {
      const codePoint: number | undefined = char.codePointAt(0);
      if (codePoint !== undefined) {
        hash = codePoint + ((hash << 5) - hash);
      }
    }
    const h: number = hash % 360;
    return `hsl(${h}, 75%, 62%)`;
  }

  private getInitials(fullName: string): string {
    const fullNameTrimmed: string = fullName.trim();
    if (!fullNameTrimmed) {
      return '';
    }
    return fullNameTrimmed
      .split(' ')
      .filter((c) => !!c.trim())
      .slice(0, 2)
      .map((part: string) => part[0])
      .join('')
      .toLowerCase();
  }

  public onAvatarLoaded(): void {
    this.avatarLoaded = true;
    this.cdr.markForCheck();
  }

  public onAvatarError(): void {
    this.resolvedAvatarUrl = '';
    this.avatarLoaded = false;
    this.cdr.markForCheck();
  }
}
